import { useState, useEffect, useCallback } from "react";
import { getOrderList, orderSearch } from "../../../../DriftRoutes/contextsApi";
import { toast } from "react-toastify";

export const useOrders = ({ showLoader, hideLoader }) => {
  const [orders, setOrders] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [nextOrderId, setNextOrderId] = useState("");

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    showLoader?.();
    try {
      console.log("Fetching orders with pageSize:", pageSize, "page:", page);
      const res = await getOrderList(pageSize, page);
      console.log("Orders API response:", res);

      if (res?.data) {
        const flatOrders = res.data.map((item) => transformOrderData(item));
        setOrders(flatOrders);
        setTotalPages(res.totalPages || 1);
        setNextOrderId(res.nextNewOrderId || "");
        setAllProducts([]); // Clear products when showing orders
      } else {
        setOrders([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      toast.error("Failed to load orders");
      setOrders([]);
    } finally {
      setLoading(false);
      hideLoader?.();
    }
  }, [pageSize, page]);

  const searchOrders = useCallback(
    async (field, value) => {
      if (!value) {
        await fetchOrders();
        return;
      }

      setSearchLoading(true);
      showLoader?.();
      try {
        console.log("Searching orders with field:", field, "value:", value);

        // For Quantity search - filter from frontend
        if (field === "Quantity") {
          // First fetch all orders for the current page
          const res = await getOrderList(pageSize, page);
          if (res?.data) {
            const searchQty = parseFloat(value);
            if (isNaN(searchQty)) {
              toast.error("Please enter a valid number for quantity search");
              setOrders([]);
              setAllProducts([]);
              return;
            }

            // Filter orders where quantity matches
            const flatOrders = res.data.map((item) => transformOrderData(item));
            const filteredOrders = flatOrders.filter(
              (order) =>
                order.quantityInPcs === searchQty ||
                order.originalQuantityInPcs === searchQty,
            );

            setOrders(filteredOrders);
            setAllProducts([]);
            setTotalPages(1);
          }
        }
        // For Design Name or Color Name search - show product level table
        else if (field === "Design_Name" || field === "Design_Colour") {
          const res = await orderSearch({
            search_field: field,
            search_value: value,
            pageSize,
            page,
          });

          console.log("Design/Color search response:", res);

          if (res?.data && Array.isArray(res.data)) {
            // Transform to product-level data
            const products = [];

            res.data.forEach((item) => {
              const orderData = item.order || item;
              const customer = orderData.customer || {};
              const orderId = orderData.neworderid || item.neworderid;

              if (orderData.product && Array.isArray(orderData.product)) {
                orderData.product.forEach((product) => {
                  const qtyPcs = Number(product.quantity_in_pieces) || 0;
                  const completedPcs =
                    Number(product.total_completed_pieces) || 0;
                  const partialPcs =
                    Number(product.partial_dispatch_quantity) || 0;
                  const totalPcs = qtyPcs + partialPcs;
                  const donePcs = completedPcs + partialPcs;
                  const progressPercentage =
                    totalPcs > 0 ? (donePcs * 100) / totalPcs : 0;

                  products.push({
                    // Product details
                    product_id: product.product_id || "",
                    prodName: product.prodName || "",
                    design_name: product.design_name || "",
                    color_name: product.color_name || "",
                    quantity_in_pieces: qtyPcs,
                    quantity_in_soft: Number(product.quantity_in_soft) || 0,
                    total_completed_pieces: completedPcs,
                    partial_dispatch_quantity: partialPcs,
                    product_image: product.product_image || [],
                    queue_ids: product.queue_ids || [],
                    progressPercentage,
                    remaining: qtyPcs - completedPcs,

                    // Order details
                    orderId: orderId,
                    masterId: item._id,
                    orderStatus: orderData.status || "New",
                    orderDate: orderData.order_date || "-",
                    tentative_date:
                      orderData.dispatch_date_production ||
                      orderData.tentative_date ||
                      "-",

                    // Customer details
                    customerName: customer.custName || "Unknown",
                    cust_id: customer.cust_id || "",
                  });
                });
              }
            });

            console.log("Transformed products:", products);
            setAllProducts(products);
            setOrders([]); // Clear orders when showing products
            setTotalPages(res.totalPages || 1);
          } else {
            setAllProducts([]);
          }
        }
        // For other search fields (Order ID, Party Name, Status, etc.)
        else {
          const res = await orderSearch({
            search_field: field,
            search_value: value,
            pageSize,
            page,
          });

          console.log("Other search response:", res);

          if (res?.data && Array.isArray(res.data)) {
            const flatOrders = res.data.map((item) => transformOrderData(item));
            setOrders(flatOrders);
            setAllProducts([]);
            setTotalPages(res.totalPages || 1);
          } else {
            setOrders([]);
            setAllProducts([]);
          }
        }
      } catch (error) {
        console.error("Search failed:", error);
        toast.error("Search failed. Please try again.");
        setOrders([]);
        setAllProducts([]);
      } finally {
        setSearchLoading(false);
        hideLoader?.();
      }
    },
    [pageSize, page, fetchOrders],
  );

  const transformOrderData = (item) => {
    // Handle both nested and flat structures
    const orderData = item.order || item;
    const customer = orderData.customer || {};
    const masterId = item._id || item.masterId;
    const neworderid = item.neworderid || orderData.neworderid;

    // Safely parse numeric values
    const totalQuantityInPcs =
      (parseFloat(orderData.total_quantity_in_pieces) || 0) +
      (parseFloat(orderData.total_partial_dispatch_quantity) || 0);

    const totalCompletedPcs =
      (parseFloat(orderData.total_completed_quantity_in_pieces) || 0) +
      (parseFloat(orderData.total_partial_dispatch_quantity) || 0);

    const progressPercentage =
      totalQuantityInPcs > 0
        ? (totalCompletedPcs / totalQuantityInPcs) * 100
        : 0;

    // Safely get party name
    const partyName =
      customer.custName || customer.name || orderData.partyName || "Unknown";

    return {
      masterId: masterId || "",
      orderId: neworderid || "",
      _id: orderData._id || "",
      partyName: partyName,
      orderDate: orderData.order_date || "-",
      dispatchDate: orderData.dispatch_date_production || "-",
      tentative_date:
        orderData.dispatch_date_production || orderData.tentative_date || "-",
      status: orderData.status || "New",
      designColour: Array.isArray(orderData.product)
        ? orderData.product
            .map((p) => `${p.design_name || ""} - ${p.color_name || ""}`)
            .filter(Boolean)
            .join(", ")
        : "-",
      quantityInPcs: totalQuantityInPcs,
      quantityInSqft: parseFloat(orderData.total_quantity_in_soft) || 0,
      originalQuantityInPcs:
        parseFloat(orderData.total_quantity_in_pieces) || 0,
      totalCompletedPcs: totalCompletedPcs,
      totalCompletedSqft:
        parseFloat(orderData.total_completed_quantity_in_soft) || 0,
      remaining_quantity_in_pieces: orderData.remaining_quantity_in_pieces || 0,
      remaining_quantity_in_sqft_pcs:
        orderData.remaining_quantity_in_sqft_pcs || 0,
      product: Array.isArray(orderData.product)
        ? orderData.product.map((p) => {
            const qtyPcs = Number(p.quantity_in_pieces) || 0;
            const completedPcs = Number(p.total_completed_pieces) || 0;
            const partialPcs = Number(p.partial_dispatch_quantity) || 0;
            const totalPcs = qtyPcs + partialPcs;
            const donePcs = completedPcs + partialPcs;

            return {
              ...p,
              product_image: p.product_image || [],
              quantity_in_pieces: qtyPcs,
              quantity_in_soft: Number(p.quantity_in_soft) || 0,
              total_completed_pieces: completedPcs,
              partial_dispatch_quantity: partialPcs,
              design_name: p.design_name || "",
              color_name: p.color_name || "",
              prodName: p.prodName || "",
              progressPercentage: totalPcs > 0 ? (donePcs * 100) / totalPcs : 0,
              remaining: qtyPcs - completedPcs,
            };
          })
        : [],
      productionUpdates: Array.isArray(orderData.product)
        ? orderData.product.flatMap((p) => p.production_updates || [])
        : [],
      cust_id: customer.cust_id || customer.id || "",
      custName: customer.custName || customer.name || "",
      phoneno: customer.phoneno || "",
      email: customer.email || "",
      address: customer.address || "",
      city: customer.city || "",
      state: customer.state || "",
      country: customer.country || "",
      customer_refrence_number: orderData.customer_refrence_number || "",
      ordertype: orderData.ordertype || "",
      box_packed: orderData.box_packed === "1" || orderData.box_packed === 1,
      progressPercentage,
      old_tentative_dates: orderData.old_tentative_dates || [],
      total_partial_dispatch_quantity:
        orderData.total_partial_dispatch_quantity || 0,
      logs: orderData.logs || [],
    };
  };

  const updateOrderInState = (orderId, updates) => {
    setOrders((prev) =>
      prev.map((order) =>
        order.orderId === orderId ? { ...order, ...updates } : order,
      ),
    );
  };

  const refreshOrders = useCallback(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return {
    orders,
    setOrders,
    allProducts,
    setAllProducts,
    loading,
    searchLoading,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    nextOrderId,
    fetchOrders,
    searchOrders,
    updateOrderInState,
    refreshOrders,
  };
};
